/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useMyPasses — the passes the signed-in visitor holds, and a way to re-read
 * them after a write.
 *
 * Shared by the event page (which offers the check-in) and the events list
 * (which only says check-in is open). One hook rather than the same effect
 * twice, because the subtleties are the ones a second copy gets wrong: asking
 * at all only when somebody is signed in, keeping `null` as "we could not
 * ask", and dropping an answer that arrives after the visitor has navigated
 * away.
 *
 * NOT react-query, unlike the public list reads beside it. This answer is
 * per-session and re-read on demand straight after a write, and a shared cache
 * entry would hand the next visitor on the same page the previous one's passes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import fetchMyPasses from '../components/athlohive/event-detail/fetchMyPasses';
import { idOf } from '../components/organizer/checkin/checkInRules';
import { isUserLoggedIn } from '../utils/utils';

/**
 * Is this pass the signed-in person's own?
 *
 * "OWNER SCOPED" IS WIDER THAN IT SOUNDS, and that is why this exists. The
 * server's ownership engine also grants an organizer every row hanging off an
 * event they created, so the read hands them their participants' passes as
 * well as their own. A participant sees no difference; an organizer who had
 * also entered an event would find other people's seats on their own check-in,
 * which is wrong and alarming in equal measure.
 *
 * A row whose owner the server did not send is NOT assumed to be somebody
 * else's: it is kept. Hiding a pass somebody holds is the worse failure — they
 * are standing at a gate — and the write is proved against the server's own
 * ownership check anyway.
 */
function isOwnedBy(row, userId) {
  const owner = idOf(row?.user_id);
  if (owner === null || owner === undefined || owner === '') return true;
  return String(owner) === String(userId);
}

export default function useMyPasses(enabled = true) {
  // null is "not asked, or could not ask" — never "holds none". A page must be
  // able to tell those apart: one draws nothing, the other could say so.
  const [passes, setPasses] = useState(null);
  const [loading, setLoading] = useState(false);
  const aliveRef = useRef(true);

  // SET TRUE ON MOUNT, not just false on unmount. In StrictMode React mounts,
  // unmounts and mounts again, so a ref that is only ever set false stays
  // false for the whole life of the second mount — and then every answer that
  // arrives is thrown away as "too late", the passes never arrive, and the
  // check-in section silently never appears. It only bites in development,
  // which is the worst kind of bug: the one that is absent in the environment
  // where you would notice it.
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const reload = useCallback(async () => {
    const who = await isUserLoggedIn().catch(() => null);
    // The read is owner scoped and answered from the session, so there is
    // nothing to ask on behalf of an anonymous visitor — and asking would
    // spend a round trip to be told 401.
    if (!who?.user_id) {
      if (aliveRef.current) { setPasses(null); setLoading(false); }
      return null;
    }
    if (aliveRef.current) setLoading(true);
    const rows = await fetchMyPasses();
    const mine = rows === null ? null : rows.filter((row) => isOwnedBy(row, who.user_id));
    if (aliveRef.current) { setPasses(mine); setLoading(false); }
    return mine;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    reload();
  }, [enabled, reload]);

  return { passes, loading, reload };
}
